import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type RouterService from '@ember/routing/router-service';
import type ObservableDocumentModel from 'tio-common/models/observable-document';
import type ObservablesService from 'tio-common/services/observables';
import type { ObservableDocumentAttributes } from 'tio-common/services/observables';
import type CableService from 'tio-common/services/cable';
import type SessionContextService from './session-context';
import type RemoteEventLoggerService from 'tio-employee/services/remote-event-logger';
import type WellnessTrainRecalculatorService from 'tio-common/services/wellness-train-recalculator';

export type ProviderParam = 'nslds' | 'statement' | 'transaction_history';

export type ObservableSource = keyof typeof OBSERVABLE_PRODUCT_ROUTES;
type BatchStatusAttributes = Partial<ObservableDocumentAttributes> & {
  extractionState?: ObservableDocumentModel['extractionState'];
  reflectionState?: ObservableDocumentModel['reflectionState'];
};
type BatchStatus = {
  id: string;
  recordAttributes: BatchStatusAttributes;
  transition: { event: string };
};

const SERVICERS = ['nelnet', 'mohela', 'aidvantage', 'edfinancial', 'americaned'];

const OBSERVABLE_PRODUCT_ROUTES = {
  slr: 'authenticated.slr.dashboard',
  pslf: 'authenticated.pslf.dashboard',
  syf: 'authenticated.syf.dashboard',
  syf_payments: 'authenticated.syf.dashboard',
  strategy_finder: 'authenticated.repaying-student-debt.repayment-strategy-finder.dashboard',
  idr: 'authenticated.idr.dashboard',
  dashboard: 'authenticated.dashboard',
};

export const getObservableProductRoute = (
  source?: keyof typeof OBSERVABLE_PRODUCT_ROUTES,
  isSlr?: boolean
): string => {
  // If source is pslf, strategy-finder, or idr, always navigate back to it
  // regardless of whether user is SLR (DEV-5705)
  if (source && ['pslf', 'strategy_finder', 'idr'].includes(source)) {
    return OBSERVABLE_PRODUCT_ROUTES[source];
  }
  // For other sources, SLR users go to SLR dashboard
  if (isSlr) {
    return OBSERVABLE_PRODUCT_ROUTES.slr;
  }

  if (source && source in OBSERVABLE_PRODUCT_ROUTES) {
    return OBSERVABLE_PRODUCT_ROUTES[source];
  }
  return 'authenticated.account-activity.accounts';
};

export const getInstitutionMatch = (institutionName = ''): string | undefined => {
  const keyMatch = SERVICERS.find((key) =>
    institutionName.toLowerCase().replace(/\s+/g, '').includes(key)
  );
  return keyMatch;
};

// how long to wait once a document is processed to transition to the confirmation
// page, so that the user can see the progress bar fill and a success message
const CONFIRMATION_TRANSITION_TIMEOUT = 1000;

export default class ObservabilityService extends Service {
  @service declare router: RouterService;
  @service declare sessionContext: SessionContextService;
  @service declare cable: CableService;
  @service declare observables: ObservablesService;
  @service declare remoteEventLogger: RemoteEventLoggerService;
  @service declare wellnessTrainRecalculator: WellnessTrainRecalculatorService;

  @tracked private subscriptionParams: object | null = null;

  navigateToConfirmation = (document: ObservableDocumentModel, source?: string) => {
    this.router.transitionTo('authenticated.observability.confirm', document.id, {
      queryParams: { source },
    });
  };

  unsubscribeFromBatchStatus = () => {
    if (this.subscriptionParams) {
      this.cable.unsubscribe(this.subscriptionParams);
      this.subscriptionParams = null;
    } else {
      console.warn('ObservabilityService: Attempted to unsubscribe when no subscription exists');
    }
  };

  subscribeToBatchStatus = (batchId: string, source?: string) => {
    const received = (msg: BatchStatus) => {
      const id = msg.id;
      const recordAttributes = msg.recordAttributes;

      const documentAttributes: ObservableDocumentAttributes = {
        ...recordAttributes,
        id,
      };

      const document = this.observables.updateObservableDocument(documentAttributes);

      const extractionFailed = ['ExtractionState.FAILED', 'ExtractionState.INVALID'].includes(
        recordAttributes.extractionState ?? ''
      );

      if (extractionFailed) {
        this.remoteEventLogger.logObservabilityEvent({
          eventName: 'PROCESSING_FAILURE',
          component: 'ObservabilityService',
          document,
        });
      }

      const reflectionSuccessful = msg.transition.event === 'content_reflected';
      const reflectionFailed = msg.transition.event === 'reflection_failed';
      const reflectionTerminal = reflectionSuccessful || reflectionFailed;

      if (reflectionTerminal) {
        this.unsubscribeFromBatchStatus();

        if (reflectionSuccessful) {
          // Trigger wellness train recalculation after successful document upload
          this.wellnessTrainRecalculator.recalculate.perform({ force: true });

          if (recordAttributes.reflectionState === 'ReflectionState.APPLIED') {
            this.remoteEventLogger.logObservabilityEvent({
              eventName: 'PROCESSING_SUCCESS',
              component: 'ObservabilityService',
              document,
            });
          } else if (recordAttributes.reflectionState === 'ReflectionState.PARTIALLY_APPLIED') {
            this.remoteEventLogger.logObservabilityEvent({
              eventName: 'PROCESSING_PARTIAL_SUCCESS',
              component: 'ObservabilityService',
              document,
            });
          }
        }

        if (reflectionFailed) {
          this.remoteEventLogger.logObservabilityEvent({
            eventName: 'PROCESSING_FAILURE',
            component: 'ObservabilityService',
            document,
          });
        }

        setTimeout(() => {
          this.navigateToConfirmation(document, source);
        }, CONFIRMATION_TRANSITION_TIMEOUT);
      }
    };

    const channelParams = {
      channel: 'ObservableDocumentBatchStatusChannel',
      batch: batchId,
    };

    this.subscriptionParams = channelParams;
    this.cable.subscribe(channelParams, {
      received: (data: unknown) => received(data as BatchStatus),
    });
  };
}
